import React, { useEffect, useState } from "react";
import { ArrowLeft, Calendar, Clock, CheckCircle, Star, ChevronDown, ChevronUp } from 'lucide-react';
import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import API_URL from '../config';
import Modal from "./Modal";
import Waitlist from "./Waitlist";

const ProgramDetail = () => {
  const [expandedModule, setExpandedModule] = useState(null);
  const { slug } = useParams(); // <-- Change from id to slug
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    axios
      .get(`${API_URL}/api/programs/${slug}`)
      .then((response) => {
        setProgram(response.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.response?.data?.error || "Program not found");
        setLoading(false);
      });
  }, [slug]);
  

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-indigo-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-indigo-600"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-indigo-50 flex justify-center items-center">
        <div className="text-red-600 text-center bg-white p-8 rounded-xl shadow-lg">
          <h2 className="text-2xl font-bold mb-2">Oops!</h2>
          <p>Error: {error}</p>
        </div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-indigo-50 flex justify-center items-center">
        <div className="text-center bg-white p-8 rounded-xl shadow-lg">
          <h2 className="text-2xl font-bold mb-2">Program Not Found</h2>
          <p className="text-gray-600">The program you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Vogue Magazine Cover Style */}
      <div className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Image with Sophisticated Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${program.image})`,
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-transparent"></div>
        
        {/* Elegant Typography Overlay */}
        <div className="relative z-10 max-w-7xl mx-auto px-8 py-20">
          {/* Navigation */}
          <div className="mb-16">
            <Link to={"/programs"}>
              <button className="group flex items-center gap-3 text-white/70 hover:text-white font-light tracking-wide transition-all duration-300">
                <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
                <span className="text-sm tracking-[0.2em] uppercase">Back to Programs</span>
              </button>
            </Link>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Column - Editorial Content */}
            <div className="space-y-12">
              {/* Category Badge */}
              <div className="flex items-center gap-4">
                <span className={`px-4 py-2 rounded-full text-xs font-medium tracking-[0.15em] uppercase border backdrop-blur-sm bg-white/10 text-white border-white/20`}>
                  {program.category}
                </span>
                {program.featured && (
                  <span className="bg-gradient-to-r from-amber-400 to-amber-500 text-black px-4 py-2 rounded-full text-xs font-bold tracking-[0.1em] uppercase flex items-center gap-2">
                    <Star size={12} fill="currentColor" />
                    Featured
                  </span>
                )}
              </div>

              {/* Main Title */}
              <div className="space-y-6">
                <h1 className="text-6xl lg:text-7xl font-thin leading-[0.9] text-white tracking-tight">
                  {program.title.split(' ')[0]}
                  <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-white/60">
                    {program.title.split(' ').slice(1).join(' ')}
                  </span>
                </h1>
                
                <div className="w-24 h-px bg-gradient-to-r from-white to-transparent"></div>
                
                <p className="text-2xl font-light text-white/80 tracking-wide leading-relaxed max-w-lg">
                  {program.subtitle}
                </p>
              </div>

              {/* Program Details */}
              <div className="flex gap-8 text-white/70">
                <div className="flex items-center gap-2">
                  <Calendar size={16} />
                  <span className="text-sm tracking-wider">{program.duration}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={16} />
                  <span className="text-sm tracking-wider">{program.format}</span>
                </div>
              </div>
            </div>

            {/* Right Column - Enrollment Card */}
            <div className="relative">
              <div className="bg-white/5 backdrop-blur-xl border border-white/20 rounded-3xl p-10">
                <div className="text-center space-y-8">
                  <div className="space-y-4">
                    <p className="text-white/60 text-sm tracking-[0.2em] uppercase">Next Experience</p>
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                      <div className="text-3xl font-light text-white tracking-wide">{program.nextCohort}</div>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <p className="text-white/60 text-sm tracking-[0.2em] uppercase">Investment</p>
                    <div className="text-4xl font-thin text-white tracking-wider">{program.price}</div>
                  </div>
                  
                  {/* LOGIC: Show button or closed message */}
                  {program.status === 'Closed' ? (
                    <div className="w-full mt-4 bg-gray-200 text-gray-500 font-medium py-4 px-8 rounded-2xl text-sm uppercase cursor-not-allowed">
                      Enrollment Closed
                    </div>
                  ) : (
                    <Link to={`/apply/${program.slug}`}>
                      <button className="w-full bg-white mt-4 text-black font-medium py-4 px-8 rounded-2xl text-sm tracking-[0.1em] uppercase hover:bg-white/90 transition-all duration-300 transform hover:scale-[1.02]">
                        Reserve Your Place
                      </button>
                    </Link>
                  )}

                  {/* Optionally, show a waitlist or notify me button */}
                  {program.status === 'Closed' && (
                    <button className="text-white/70 hover:text-white text-sm uppercase transition-colors mt-2 transition-all duration-300 transform hover:scale-[1.02]"
                    onClick={() => setWaitlistOpen(true)}>
                      Join Waitlist  →
                    </button>
                  )}
                  {program.price !== 'Free' && program.status === 'Open' && (
                      <button className="text-white/70 hover:text-white text-sm uppercase transition-colors">
                        <Link to="/consultation" className="mt-2">
                          Private Consultation →
                        </Link>
                      </button>
                  )}

                </div>
              </div>
            </div>
            <Modal open={waitlistOpen} onClose={() => setWaitlistOpen(false)}>
              <Waitlist defaultProgram={program.title} />
            </Modal>

            {successMessage && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 bg-green-600 text-white px-6 py-3 rounded-xl shadow-lg z-[99999] transition-all">
          {successMessage}
        </div>
      )}
          </div>
        </div>
      </div>

      {/* Editorial Description Section */}
      <div className="py-10 bg-black text-white">
        <div className="max-w-5xl mx-auto px-8">
          <div className="text-center">
            <p className="text-xs tracking-[0.3em] uppercase text-white/70 mb-2">Program Overview</p>
            <div className="text-8xl font-thin text-white/40">"</div>
          </div>
          <blockquote className="text-xl md:text-xl font-light leading-relaxed text-center tracking-wide">
            {program.description}
          </blockquote>
        </div>
      </div>

      {/* Learning Outcomes - Magazine Feature Style */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            {/* Left - Title */}
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-gray-400 mb-8">Transformation Outcomes</p>
              <h2 className="text-5xl font-thin leading-tight mb-12">
                What You'll
                <br />
                <em className="text-gray-400">Master</em>
              </h2>
              <div className="w-24 h-px bg-black"></div>
            </div>

            {/* Right - Outcomes */}
            <div className="space-y-2">
              {program.outcomes.map((outcome, idx) => (
                <div key={idx} className="flex items-start gap-4 p-6 border-l-2 border-gray-100 hover:border-black transition-colors">
                  <CheckCircle size={20} className="text-black mt-1 flex-shrink-0" />
                  <span className="text-lg font-light text-gray-800 leading-relaxed">{outcome}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Curriculum - Editorial Layout */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-8">
          <div className="text-center mb-20">
            <p className="text-xs tracking-[0.3em] uppercase text-gray-400 mb-8">Curriculum</p>
            <h2 className="text-4xl md:text-5xl font-thin leading-tight">
              The Journey of
              <br />
              <em>Excellence</em>
            </h2>
          </div>

          <div className="space-y-4">
            {program.modules.map((module, idx) => (
              <div key={idx} className="bg-white rounded-3xl overflow-hidden shadow-sm">
                <div
                  className="p-8 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedModule(expandedModule === idx ? null : idx)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-6">
                      <div className="w-16 h-16 bg-black text-white rounded-2xl flex items-center justify-center">
                        <span className="text-xl font-light">{String(idx + 1).padStart(2, '0')}</span>
                      </div>
                      <div>
                        <h3 className="text-2xl font-light text-gray-900 mb-2">{module.title}</h3>
                        <p className="text-gray-500 tracking-wide">{module.duration}</p>
                      </div>
                    </div>
                    <div className="text-gray-400">
                      {expandedModule === idx ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                    </div>
                  </div>
                </div>

                {expandedModule === idx && (
                  <div className="px-8 pb-8 border-t border-gray-100">
                    <div className="grid md:grid-cols-2 gap-12 pt-8">
                      {/* What You'll Learn */}
                      <div>
                        <h4 className="text-lg font-medium text-gray-900 mb-4 tracking-wide">What You'll Learn</h4>
                        <p className="text-gray-600 leading-relaxed mb-4">{module.whatYoullLearn.overview}</p>
                        <ul className="space-y-2">
                          {module.whatYoullLearn.objectives.map((obj, i) => (
                            <li key={i} className="flex items-start gap-3">
                              <div className="w-1 h-1 bg-black rounded-full mt-3 flex-shrink-0"></div>
                              <span className="text-gray-700 font-light">{obj}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Skills */}
                      <div>
                        <h4 className="text-lg font-medium text-gray-900 mb-4 tracking-wide">Skills You'll Gain</h4>
                        <div className="flex flex-wrap gap-3">
                          {module.skillsYoullGain.map((skill, i) => (
                            <span key={i} className="bg-gray-100 text-gray-800 px-4 py-2 rounded-full text-sm font-light tracking-wide">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Program Details */}
      <div className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          <div className="grid lg:grid-cols-3 gap-16">
            {/* Prerequisites */}
            <div className="text-center">
              <div className="w-px h-20 bg-gradient-to-b from-transparent via-black to-transparent mx-auto mb-8"></div>
              <h3 className="text-xl font-light mb-6 tracking-wide">Prerequisites</h3>
              <p className="text-gray-600 leading-relaxed font-light">
                {program.prerequisites}
              </p>
            </div>

            {/* Certification */}
            <div className="text-center">
              <div className="w-px h-20 bg-gradient-to-b from-transparent via-black to-transparent mx-auto mb-8"></div>
              <h3 className="text-xl font-light mb-6 tracking-wide">Certification</h3>
              <p className="text-gray-600 leading-relaxed font-light">
                {program.certification}
              </p>
            </div>

            {/* Investment */}
            <div className="text-center">
              <div className="w-px h-20 bg-gradient-to-b from-transparent via-black to-transparent mx-auto mb-8"></div>
              <h3 className="text-xl font-light mb-6 tracking-wide">Investment</h3>
              <p className="text-3xl font-thin text-gray-900 tracking-wider">
                {program.price}
              </p>
            </div>
          </div>
        </div>
      </div>

    {/* Final CTA - Editorial Style */}
    <div className="py-14 bg-gray-100 text-black">
      <div className="max-w-4xl mx-auto px-8 text-center">
        <p className="text-xs tracking-[0.3em] uppercase text-black/50 mb-8">Begin Your Transformation</p>
        <h2 className="text-4xl md:text-5xl font-thin leading-tight mb-12">
          Ready to Join the
          <br />
          <em>Elite?</em>
        </h2>
        
        <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
          {program.status === 'Closed' ? (
            <button className="bg-gray-300 text-gray-500 px-12 py-4 text-sm tracking-[0.2em] uppercase font-medium rounded-xl"
            onClick={() => setWaitlistOpen(true)}>
              Join Waitlist  →
            </button>
          ) : (
            <Link to={`/apply/${program.slug}`} className="bg-black text-white px-12 py-4 text-sm tracking-[0.2em] uppercase font-medium transition-all duration-300 transform hover:scale-[1.02]">
              Secure Your Place
            </Link>
          )}
          <div className="w-px h-12 bg-white/20 hidden sm:block"></div>
          {program.price !== 'Free' && program.status === 'Open' && (
            <Link to={"/consultation"}>
              <button className="text-black/70 hover:text-black text-sm tracking-[0.2em] uppercase transition-colors">
                Schedule Private Consultation →
              </button>
            </Link>
          )}
        </div>
      </div>
    </div>
    </div>
  );
};

export default ProgramDetail;